import { PeriodBreakdownItem, SportBreakdownEntry, StatsResponse } from '../../models/stats.models';
import {
  MetricMode,
  formatDistributionPercent,
  formatDistributionRawValue,
  formatStackedChartTooltipValue,
  formatYAxisTick,
  getDurationDistanceMetric,
  toChartValue,
} from '../../pages/training-log/training-log.helpers';
import { getSportLayerColor } from '../../pages/training-log/training-log.helpers';
import { getDistanceAxisDomain, getDurationAxisDomain } from '../season-overview-chart/season-overview-chart.helpers';

export type { MetricMode };
export { formatDistributionPercent, formatDistributionRawValue, formatYAxisTick };
export { formatStackedChartTooltipValue as formatSportChartValue };

export interface StackedSportChartRow {
  label: string;
  [sportKey: string]: string | number;
}

export interface SportDistributionItem {
  key: string;
  label: string;
  percent: number;
  rawValue: number;
  color: string;
}

export const getSportKeysFromPeriodBreakdown = (
  periods: PeriodBreakdownItem[],
  sportBreakdown: Record<string, SportBreakdownEntry>
): string[] => {
  const keys = new Set<string>();
  periods.forEach((period) => {
    Object.keys(period.sport ?? {}).forEach((key) => keys.add(key));
  });
  Object.keys(sportBreakdown ?? {}).forEach((key) => keys.add(key));

  return Array.from(keys).sort((a, b) => {
    const aTotal = getDurationDistanceMetric(sportBreakdown?.[a], 'duration');
    const bTotal = getDurationDistanceMetric(sportBreakdown?.[b], 'duration');
    return bTotal - aTotal;
  });
};

export const buildPeriodSportChartData = (
  periods: PeriodBreakdownItem[],
  sportKeys: string[],
  mode: MetricMode
): StackedSportChartRow[] => {
  return periods.map((period) => {
    const row: StackedSportChartRow = { label: `P${period.index}` };

    sportKeys.forEach((sportKey) => {
      const rawValue = getDurationDistanceMetric(period.sport?.[sportKey], mode);
      row[sportKey] = toChartValue(rawValue, mode);
    });

    return row;
  });
};

export const buildSeasonSportDistribution = (
  sportBreakdown: Record<string, SportBreakdownEntry>,
  sportKeys: string[],
  mode: MetricMode
): SportDistributionItem[] => {
  const entries = sportKeys.map((key, index) => {
    const rawValue = getDurationDistanceMetric(sportBreakdown?.[key], mode);
    return {
      key,
      label: key,
      rawValue,
      color: getSportLayerColor(key, index),
    };
  });

  const total = entries.reduce((sum, entry) => sum + entry.rawValue, 0);

  return entries
    .map((entry) => ({
      ...entry,
      percent: total > 0 ? (entry.rawValue / total) * 100 : 0,
    }))
    .sort((a, b) => b.rawValue - a.rawValue);
};

export const getStackedSportRowTotal = (row: StackedSportChartRow, sportKeys: string[]): number => {
  return sportKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0);
};

export const getStackedSportYDomain = (
  rows: StackedSportChartRow[],
  sportKeys: string[],
  mode: MetricMode
): [number, number] => {
  const totals = rows.map((row) => getStackedSportRowTotal(row, sportKeys));
  return mode === 'duration' ? getDurationAxisDomain(totals) : getDistanceAxisDomain(totals);
};

export const hasSportData = (stats: StatsResponse | null): boolean => {
  if (!stats) {
    return false;
  }

  const seasonTotal = Object.values(stats.sportBreakdown ?? {}).reduce(
    (sum, entry) => sum + (entry.duration ?? 0) + (entry.distance ?? 0),
    0
  );
  if (seasonTotal > 0) {
    return true;
  }

  return (stats.periodBreakdown ?? []).some((period) =>
    Object.values(period.sport ?? {}).some((entry) => (entry.duration ?? 0) > 0 || (entry.distance ?? 0) > 0)
  );
};
